/**
 * CLE-2904 — what Copy on the contracts table carries onto the new contract, beyond the rates,
 * carveouts and rules it always carried: the notes, the implant terms, the contract facts, the
 * connected providers, and the attachment files. Pure, so each rule is tested without a store or
 * a DOM.
 *
 * The files are copied by the API on save, each to the new contract's own object; the page only
 * says which of the source's files to take, and reports the ones that could not be copied.
 */

import { dateOnlyStringToCalendarDate } from "helpers/date";
import {
  IAttachment,
  IAttachmentsCopySummary,
  IContractFeeSchedule,
  IContractType,
} from "store/queries/contractFeeSchedules";

import { withoutContractProvider } from "./linkedProviders";

/** The slice of the create form's state a copy pre-fills from the source contract. */
export interface ICopiedContractFields {
  notes: string;
  implantTerms: string;
  contractType: IContractType | null;
  signedDate: Date | null;
  timelyFilingDays: number | null;
  inpatientReimbursement: string;
  outpatientReimbursement: string;
  facilityReimbursement: string;
  nonFacilityReimbursement: string;
  multipleProcedureMethodology: string;
}

/**
 * The source's notes, implant terms and contract facts, mapped into form state exactly as the
 * edit page maps a contract it loads: text as `""` when absent, the rest as `null`, and the
 * signed date as a local calendar day (the column is a DATE, never an instant).
 */
export const copiedContractFields = (
  source: Pick<
    IContractFeeSchedule,
    | "notes"
    | "implantTerms"
    | "contractType"
    | "signedDate"
    | "timelyFilingDays"
    | "inpatientReimbursement"
    | "outpatientReimbursement"
    | "facilityReimbursement"
    | "nonFacilityReimbursement"
    | "multipleProcedureMethodology"
  >
): ICopiedContractFields => ({
  notes: source.notes || "",
  implantTerms: source.implantTerms || "",
  contractType: source.contractType ?? null,
  signedDate: dateOnlyStringToCalendarDate(source.signedDate),
  timelyFilingDays: source.timelyFilingDays ?? null,
  inpatientReimbursement: source.inpatientReimbursement || "",
  outpatientReimbursement: source.outpatientReimbursement || "",
  facilityReimbursement: source.facilityReimbursement || "",
  nonFacilityReimbursement: source.nonFacilityReimbursement || "",
  multipleProcedureMethodology: source.multipleProcedureMethodology || "",
});

/**
 * The source's connected providers, for the copy's own selection. Ani asked for them to be
 * carried (CLE-2904). The source's own primary provider is dropped if it ever appears: a
 * contract cannot connect its own provider, and the API refuses one that tries.
 */
export const copiedLinkedProviderIds = (
  source: Pick<IContractFeeSchedule, "providerId" | "linkedProviders">
): string[] =>
  Array.from(
    new Set(
      (source.linkedProviders ?? [])
        .map((provider) => provider.id)
        .filter((id) => id !== source.providerId)
    )
  );

/** A local calendar day as one comparable number (YYYYMMDD). */
const calendarDayKey = (date: Date) =>
  date.getFullYear() * 10000 + (date.getMonth() + 1) * 100 + date.getDate();

/**
 * Whether a new, open-ended contract starting on `effectiveDate` overlaps a source that ends on
 * `sourceEndDate`: unless the source's end day is strictly before the effective day. False when
 * either day is unknown, so the hint stays quiet rather than guess.
 */
const termsOverlapSource = (
  sourceEndDate: string,
  effectiveDate: Date | null | undefined
): boolean => {
  const sourceEnd = dateOnlyStringToCalendarDate(sourceEndDate);
  if (!sourceEnd || !effectiveDate) return false;
  return !(calendarDayKey(sourceEnd) < calendarDayKey(effectiveDate));
};

/**
 * The connected providers the API will refuse when this copy is saved: the ones still
 * connected to the source that the copy keeps, when the copy keeps the source's payer, the
 * source is live, and the two terms overlap. The API refuses a provider connected to another
 * live contract (not Inactive, not Expired) for the same payer over overlapping dates.
 *
 * Overlap mirrors the API's `termsOverlap` (`_shared/linkedProviders.ts`). The new contract is
 * `{ effectiveDate: <the form's date>, endDate: null }`, so it overlaps the source unless the
 * source's end day is strictly before the form's effective day — the same day overlaps. An
 * open-ended source always overlaps. A source with an end date and a form with no effective date
 * cannot be judged, so this says nothing: the hint must never claim a refusal that would not
 * happen. Advisory only: the API stays the authority.
 */
export const copiedProvidersBlockedBySource = (
  copyFromId: string | null | undefined,
  source:
    | Pick<
        IContractFeeSchedule,
        "payerId" | "status" | "endDate" | "linkedProviders"
      >
    | null
    | undefined,
  selectedPayerIds: number[],
  linkedProviderIds: string[],
  effectiveDate: Date | null | undefined
): string[] => {
  if (!copyFromId || !source) return [];
  if (selectedPayerIds.length !== 1 || selectedPayerIds[0] !== source.payerId)
    return [];
  if (source.status === "Inactive" || source.status === "Expired") return [];
  if (source.endDate && !termsOverlapSource(source.endDate, effectiveDate))
    return [];
  return (source.linkedProviders ?? [])
    .filter((provider) => linkedProviderIds.includes(provider.id))
    .map((provider) => provider.name);
};

/** One provider or several, for the sentence below. */
const pronounsFor = (count: number) =>
  count === 1
    ? { subject: "it is", object: "it" }
    : { subject: "they are", object: "them" };

/** The inline warning for `copiedProvidersBlockedBySource`'s names. */
export const copiedProvidersBlockedWarning = (
  names: string[],
  payerName: string | undefined
): string => {
  const { subject, object } = pronounsFor(names.length);
  return `${names.join(
    ", "
  )} will be refused on save: ${subject} already connected to the source contract for ${
    payerName ?? "this payer"
  }. Change the payer, or remove ${object} here.`;
};

/** Shown in place of the connected-providers picker when a copy turns into a bulk create. */
export const COPIED_PROVIDERS_NOT_ON_BULK =
  "Connected providers are not copied when creating several contracts at once. Add them on each contract after saving.";

/**
 * The connected-providers picker's value. When copying, a carried provider can become the copy's
 * own provider (she switches Provider to it); the options drop it, so a value still holding its
 * id would render as a nameless chip with only an "x" (the CLE-2800 bug). Display only: the save
 * already strips it (`withoutContractProvider`). When the page is not copying, the list is
 * returned as it is, the same array, so nothing changes there.
 */
export const connectedProviderPickerValue = (
  copyFromId: string | null | undefined,
  linkedProviderIds: string[],
  primaryProviderId: string | undefined
): string[] =>
  copyFromId
    ? withoutContractProvider(linkedProviderIds, primaryProviderId)
    : linkedProviderIds;

export interface IAttachmentCopyPayload {
  copyAttachmentsFromContractId?: string;
  copyAttachmentIds?: string[];
}

/**
 * The create body's attachment-copy fields: the source contract, and the ids of its files the
 * user left ticked.
 *
 * Never an empty id list. When nothing is to be copied — no copy source, a source with no files,
 * or every file unticked — neither field is sent. The API reads an omitted list as "all of them",
 * and the bulk path's multipart body cannot carry an empty array, so an empty list could arrive
 * as "copy everything the user unticked".
 */
export const attachmentCopyPayload = (
  copyFromId: string | null | undefined,
  sourceAttachments: Pick<IAttachment, "id">[] | null | undefined,
  excludedIds: string[]
): IAttachmentCopyPayload => {
  if (!copyFromId) return {};
  const excluded = new Set(excludedIds);
  const ids = (sourceAttachments ?? [])
    .map((attachment) => attachment.id)
    .filter((id) => !excluded.has(id));
  if (ids.length === 0) return {};
  return { copyAttachmentsFromContractId: copyFromId, copyAttachmentIds: ids };
};

interface IWithAttachmentsCopySummary {
  attachmentsCopySummary?: IAttachmentsCopySummary | null;
}

/**
 * The warning to show after a create when some attachment files could not be copied, or `null`
 * when nothing failed. A failed copy must never be silent: the contract was saved without that
 * file, and only an upload by hand puts it back.
 *
 * Reads both create responses: a single create (`data` is the contract) and a bulk create
 * (`data` is every contract, each with its own summary). A file that failed on several
 * contracts is named once.
 *
 * `requestedCount` is how many files the page asked for. The API names every file it tried and
 * could not copy, but a file removed from the source between loading the page and saving, or a
 * source list it could not read, never reaches the summary at all — so the page also compares
 * what it asked for with what each contract's summary accounts for (copied + not copied), and
 * reports the largest shortfall. 0 or omitted: only the named failures, as before.
 */
export const attachmentCopyWarning = (
  result:
    | {
        data?:
          | IWithAttachmentsCopySummary
          | IWithAttachmentsCopySummary[]
          | null;
      }
    | null
    | undefined,
  requestedCount = 0
): string | null => {
  const data = result?.data;
  const contracts = Array.isArray(data) ? data : [data];
  // Two files on one contract can share a name, so a name counts as often as it failed on any ONE
  // contract; across the contracts of a bulk create the same file is counted once.
  const failedByName = new Map<string, number>();
  contracts.forEach((contract) => {
    const onThisContract = new Map<string, number>();
    (contract?.attachmentsCopySummary?.notCopied ?? []).forEach((name) =>
      onThisContract.set(name, (onThisContract.get(name) ?? 0) + 1)
    );
    onThisContract.forEach((count, name) =>
      failedByName.set(name, Math.max(failedByName.get(name) ?? 0, count))
    );
  });
  const names = Array.from(failedByName.keys());
  const failedCount = Array.from(failedByName.values()).reduce(
    (sum, count) => sum + count,
    0
  );
  const unaccounted = Math.max(
    0,
    ...contracts.map((contract) => {
      const summary = contract?.attachmentsCopySummary;
      const accounted =
        (summary?.copied.length ?? 0) + (summary?.notCopied.length ?? 0);
      return requestedCount - accounted;
    })
  );
  const named =
    names.length > 0
      ? `${failedCount} attachment(s) could not be copied: ${names.join(
          ", "
        )}. Upload them from the contract page.`
      : null;
  if (named && unaccounted > 0)
    return `${named} ${unaccounted} more were not copied; check the new contract's attachments.`;
  if (named) return named;
  if (unaccounted > 0)
    return `${unaccounted} attachment(s) were not copied. Check the new contract's attachments and upload any that are missing.`;
  return null;
};
